<script setup lang="ts">
/**
 * Отчёт: итоговая оценка и сигналы по категориям.
 *
 * Сигналы приходят с сервера уже отсортированными по значимости. Здесь они
 * группируются по категориям, чтобы читалось как разбор по темам, а нейтральные
 * факты (severity `info`) убираются под спойлер — иначе они тонут важные
 * находки в шуме.
 */

import { computed, ref } from 'vue';
import type { Report, Signal, SignalCategory, SignalSeverity } from '@vpncheck/shared';

const props = defineProps<{ report: Report; token: string | null }>();
defineEmits<{ restart: [] }>();

const showInfo = ref(false);

const VERDICT_TEXT: Record<Report['verdict'], { title: string; hint: string }> = {
  clean: {
    title: 'VPN не обнаружен',
    hint: 'Проверки не нашли признаков туннеля. Так выглядит обычное подключение.',
  },
  suspicious: {
    title: 'Есть заметные признаки',
    hint: 'По отдельности каждый признак слабый, но вместе они выделяют вас среди обычных пользователей.',
  },
  exposed: {
    title: 'VPN виден однозначно',
    hint: 'Найдены признаки, по которым использование VPN определяется без сомнений.',
  },
};

const CATEGORY_TITLES: Record<SignalCategory, string> = {
  ip: 'Выходной IP',
  'split-tunnel': 'Сплит-туннелирование',
  'blocked-access': 'Доступ к заблокированному',
  webrtc: 'WebRTC',
  'client-env': 'Окружение браузера',
  latency: 'Задержки',
  headers: 'HTTP-заголовки',
  ports: 'Открытые порты',
};

const SEVERITY_LABELS: Record<SignalSeverity, string> = {
  high: 'выдаёт однозначно',
  medium: 'заметно',
  low: 'слабый признак',
  info: 'справочно',
};

/** Категории в порядке появления, каждая со своими сигналами. */
const grouped = computed(() => {
  const visible = props.report.signals.filter((s) => showInfo.value || s.severity !== 'info');
  const order: SignalCategory[] = [];
  const buckets = new Map<SignalCategory, Signal[]>();

  for (const signal of visible) {
    if (!buckets.has(signal.category)) {
      buckets.set(signal.category, []);
      order.push(signal.category);
    }
    buckets.get(signal.category)?.push(signal);
  }

  return order.map((category) => ({
    category,
    title: CATEGORY_TITLES[category],
    signals: buckets.get(category) ?? [],
  }));
});

const infoCount = computed(
  () => props.report.signals.filter((s) => s.severity === 'info').length,
);

const reportLink = computed(() =>
  props.token ? `${window.location.origin}${window.location.pathname}?token=${props.token}` : null,
);

const copied = ref(false);

async function copyLink(): Promise<void> {
  if (!reportLink.value) return;
  try {
    await navigator.clipboard.writeText(reportLink.value);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch {
    // Буфер обмена недоступен (нет разрешения или не HTTPS) — ссылка видна рядом.
  }
}
</script>

<template>
  <section class="report">
    <div :class="['verdict', report.verdict]">
      <div class="score" :aria-label="`Оценка ${report.score} из 100`">
        <span class="value">{{ report.score }}</span>
        <span class="max">/100</span>
      </div>
      <div class="verdict-text">
        <h2>{{ VERDICT_TEXT[report.verdict].title }}</h2>
        <p>{{ VERDICT_TEXT[report.verdict].hint }}</p>
      </div>
    </div>

    <div v-if="report.incomplete.length > 0" class="card incomplete">
      <h3>Эти проверки не выполнены</h3>
      <ul>
        <li v-for="item in report.incomplete" :key="item">{{ item }}</li>
      </ul>
      <p class="hint">
        Оценка считается только по выполненным проверкам, поэтому реальная картина может
        быть хуже.
      </p>
    </div>

    <div v-for="group in grouped" :key="group.category" class="group">
      <h3 class="group-title">{{ group.title }}</h3>
      <article
        v-for="signal in group.signals"
        :key="signal.id"
        :class="['card', 'signal', signal.severity]"
      >
        <header>
          <h4>{{ signal.title }}</h4>
          <span :class="['badge', signal.severity]">{{ SEVERITY_LABELS[signal.severity] }}</span>
        </header>
        <p class="details">{{ signal.details }}</p>
        <p v-if="signal.explanation" class="explanation">{{ signal.explanation }}</p>
      </article>
    </div>

    <div class="actions">
      <button type="button" class="secondary" @click="showInfo = !showInfo">
        {{ showInfo ? 'Скрыть справочные пункты' : `Показать справочные пункты (${infoCount})` }}
      </button>
      <button type="button" class="primary" @click="$emit('restart')">Проверить снова</button>
    </div>

    <p v-if="reportLink" class="permalink">
      Ссылка на этот отчёт: <code>{{ reportLink }}</code>
      <button type="button" class="link" @click="copyLink">
        {{ copied ? 'скопировано' : 'скопировать' }}
      </button>
    </p>
  </section>
</template>

<style scoped>
.report {
  margin-top: 24px;
}

.card {
  padding: 16px 18px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
}

.verdict {
  display: flex;
  gap: 20px;
  align-items: center;
  padding: 20px;
  border: 1px solid var(--border);
  border-left-width: 4px;
  border-radius: 12px;
  background: var(--surface);
}

.verdict.clean {
  border-left-color: var(--severity-none);
}

.verdict.suspicious {
  border-left-color: var(--severity-medium);
}

.verdict.exposed {
  border-left-color: var(--severity-high);
}

.score {
  display: flex;
  align-items: baseline;
  font-variant-numeric: tabular-nums;
}

.score .value {
  font-size: 44px;
  font-weight: 650;
  letter-spacing: -0.02em;
}

.score .max {
  color: var(--text-muted);
  font-size: 16px;
}

.verdict-text h2 {
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 600;
}

.verdict-text p {
  margin: 0;
  color: var(--text-muted);
  line-height: 1.5;
}

.incomplete {
  margin-top: 16px;
}

.incomplete h3 {
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 600;
}

.incomplete ul {
  margin: 0 0 10px;
  padding-left: 20px;
  color: var(--text-muted);
  line-height: 1.6;
}

.incomplete .hint {
  margin: 0;
  color: var(--text-muted);
  font-size: 14px;
}

.group {
  margin-top: 28px;
}

.group-title {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
}

.signal {
  margin-bottom: 10px;
  border-left-width: 3px;
}

.signal.high {
  border-left-color: var(--severity-high);
}

.signal.medium {
  border-left-color: var(--severity-medium);
}

.signal.low {
  border-left-color: var(--severity-low);
}

.signal.info {
  border-left-color: var(--border);
}

.signal header {
  display: flex;
  gap: 12px;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 8px;
}

.signal h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.badge {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  white-space: nowrap;
  background: var(--surface-muted);
  color: var(--text-muted);
}

.badge.high {
  background: color-mix(in srgb, var(--severity-high) 16%, transparent);
  color: var(--severity-high);
}

.badge.medium {
  background: color-mix(in srgb, var(--severity-medium) 18%, transparent);
  color: var(--severity-medium);
}

.details {
  margin: 0;
  line-height: 1.55;
}

.explanation {
  margin: 8px 0 0;
  color: var(--text-muted);
  font-size: 14px;
  line-height: 1.6;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 28px;
}

.primary,
.secondary {
  padding: 9px 16px;
  border-radius: 8px;
  font: inherit;
  font-weight: 550;
  cursor: pointer;
}

.primary {
  border: 0;
  background: var(--accent);
  color: #fff;
}

.primary:hover {
  background: var(--accent-hover);
}

.secondary {
  border: 1px solid var(--border);
  background: transparent;
  color: var(--text);
}

.secondary:hover {
  background: var(--surface-muted);
}

.permalink {
  margin: 16px 0 0;
  color: var(--text-muted);
  font-size: 14px;
  line-height: 1.6;
  word-break: break-all;
}

.permalink code {
  font-size: 13px;
}

.link {
  margin-left: 6px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--accent);
  font: inherit;
  cursor: pointer;
  text-decoration: underline;
}
</style>
